import axios from 'axios';

/**
 * Posts a file straight to object storage using a presigned POST.
 *
 * Not the only helper that does this: `shared/lib/file-upload/uploadFileToS3` also
 * presigned-POSTs to S3 and keeps its own callers. Reach for the one here unless you need
 * that one's S3 `<Code>` parsing, which turns `EntityTooLarge`, `AccessDenied`,
 * `SignatureDoesNotMatch` and `ExpiredToken` into a short translated message a toast can
 * render; this helper has no equivalent, so a caller relying on it would regress by moving.
 * They are not folded together because `boundaries` types `shared/lib` as `lib`, which may
 * not import `shared`, so the sibling cannot delegate here. The reverse edge is open, but
 * having this helper delegate its POST would change what every caller here throws, so that
 * belongs in its own change.
 *
 * Two details are load-bearing, and are the reason the FormData assembly is not
 * re-typed per caller:
 *
 *   - Every entry of `uploadFields` is appended BEFORE the file. S3 reads the
 *     policy and signature fields as it streams the multipart body, so a request
 *     whose file part arrives ahead of them is rejected.
 *   - The request must NOT go through an authenticated client. The target is a
 *     third-party object store and the signature inside `uploadFields` is the
 *     whole authorisation, so attaching our bearer token would hand a credential
 *     to someone with no business holding one.
 *
 * `onUploadProgress(loaded, total)` reports byte-level progress. The adapter drops
 * events with a falsy `total` (S3 fires those during the handshake, and they would
 * propagate NaN into a percentage) and events arriving after `abortSignal.aborted`,
 * so a cancelled upload never ticks forward. It is attached only when a callback
 * was supplied — otherwise axios receives a config with no `onUploadProgress` key.
 */
export const uploadToS3 = async (
  uploadUrl: string,
  uploadFields: Record<string, unknown>,
  file: File,
  abortSignal?: AbortSignal,
  onUploadProgress?: (loadedBytes: number, totalBytes: number) => void,
): Promise<void> => {
  const formData = new FormData();

  Object.entries(uploadFields).forEach(([key, value]) => {
    formData.append(key, String(value));
  });
  formData.append('file', file);

  await axios.post(uploadUrl, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    signal: abortSignal,
    ...(onUploadProgress
      ? {
          onUploadProgress: (event: { loaded: number; total?: number }) => {
            if (abortSignal?.aborted) return;
            if (!event.total) return;
            onUploadProgress(event.loaded, event.total);
          },
        }
      : {}),
  });
};
